import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin } from 'lucide-react';
import { Avatar, AvatarFallback } from './Avatar';
import { Badge } from './Badge';
import { experts } from '../data/experts';
import type { BakEvent } from '../types/event';
import { formatDate, getInitials } from '../utils/expert';
import { daysUntil, summarizeInvitations } from '../utils/event';
type EventCardProps = {
  event: BakEvent;
};
export function EventCard({
  event
}: EventCardProps) {
  const s = summarizeInvitations(event);
  const days = daysUntil(event.date);
  const pct = (n: number) => s.total ? n / s.total * 100 : 0;
  const accepted = event.invitations.filter(i => i.status === 'Accepted');
  return <Link to={`/events/${event.id}`} className="group flex flex-col rounded-xl border bg-card p-5 transition-colors hover:border-foreground/20 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" data-id="element-201" data-mp="22pec1rzvpe66">
      <div className="flex items-start justify-between gap-3" data-id="element-202" data-mp="37oowvbguequz">
        <div className="min-w-0" data-id="element-203" data-mp="37m2nd6ae61a4">
          <h3 className="text-sm font-semibold" data-id="element-204" data-mp="3cmqdzqupkg3t">{event.name}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-id="element-205" data-mp="3ckx6yag6ntva">
            <span className="inline-flex items-center gap-1" data-id="element-206" data-mp="3hgckf1aa3nr6"><CalendarDays className="size-3" aria-hidden="true" data-id="element-207" data-mp="3leimhw069f5r" />{formatDate(event.date)}</span>
            <span className="inline-flex items-center gap-1" data-id="element-208" data-mp="2oqcldj2gl4w8"><MapPin className="size-3" aria-hidden="true" data-id="element-209" data-mp="2ornp5ivwmcnc" />{event.location}</span>
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0" data-id="element-210" data-mp="2xpzd1d5dywc3">
          {days >= 0 ? `In ${days} days` : 'Completed'}
        </Badge>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-211" data-mp="32q57t26ycyye">
        <Badge variant="outline" data-id="element-212" data-mp="32mpvu88ks617">{event.category}</Badge>
        <Badge variant="outline" data-id="element-213" data-mp="cyywr6ypmevg">{event.format}</Badge>
      </div>

      <div className="mt-5" data-id="element-214" data-mp="hybskh7k6wa6">
        <div className="flex items-center justify-between text-xs" data-id="element-215" data-mp="hxjvf1ybcqhs">
          <span className="font-medium" data-id="element-216" data-mp="2e1gifsf0t4i3">Expert invitations</span>
          <span className="text-muted-foreground tabular-nums" data-id="element-217" data-mp="2e3ud405sgep4">{s.total} sent</span>
        </div>
        <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true" data-id="element-218" data-mp="2a2qyfgzh8oph">
          <div className="bg-emerald-500 transition-all" style={{
          width: `${pct(s.accepted)}%`
        }} data-id="element-219" data-mp="2ny30yajvjjgy" />
          <div className="bg-amber-400 transition-all" style={{
          width: `${pct(s.awaiting)}%`
        }} data-id="element-220" data-mp="2iz9peno5e2jf" />
          <div className="bg-red-400 transition-all" style={{
          width: `${pct(s.declined)}%`
        }} data-id="element-221" data-mp="1v9fa7zgcw1w0" />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-id="element-222" data-mp="1qaknimf9pt6m">
          <span className="inline-flex items-center gap-1.5" data-id="element-223" data-mp="2534mdavdxei9"><span className="size-2 rounded-full bg-emerald-500" data-id="element-224" data-mp="209jum0kdxgri" />{s.accepted} accepted</span>
          <span className="inline-flex items-center gap-1.5" data-id="element-225" data-mp="1ck8u8k6y5odw"><span className="size-2 rounded-full bg-amber-400" data-id="element-226" data-mp="1cixqgkdi4gms" />{s.awaiting} awaiting</span>
          <span className="inline-flex items-center gap-1.5" data-id="element-227" data-mp="17jtg3qeuccuv"><span className="size-2 rounded-full bg-red-400" data-id="element-228" data-mp="3j90z5a5ztoi7" />{s.declined} declined</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border-t pt-4" data-id="element-229" data-mp="3o113rfx0d5l4">
        {accepted.length > 0 ? <div className="flex -space-x-2" data-id="element-230" data-mp="3aa5u41eoqkgh">
            {accepted.slice(0, 4).map(inv => {
          const ex = experts.find(e => e.id === inv.expertId);
          return ex ? <Avatar key={inv.id} size="sm" className="ring-2 ring-card" data-id="element-231" data-mp="5vo4rwivm29q">
                  <AvatarFallback className="text-[10px]" data-id="element-232" data-mp="atpp0ks1g7ms">{getInitials(ex.name)}</AvatarFallback>
                </Avatar> : null;
        })}
          </div> : <span className="text-xs text-muted-foreground" data-id="element-233" data-mp="1s9nz3n45hv8">No confirmed experts yet</span>}
        <span className="text-xs font-medium group-hover:underline" data-id="element-234" data-mp="2hje27g4nkhyy">Manage →</span>
      </div>
    </Link>;
}